import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Palette, Check, Pencil, Sparkles, Type, Square, MousePointerClick, Layers, Eye, EyeOff, Timer, Video, LayoutGrid, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from './ImageUpload';
import { useAuth } from '@/context/AuthContext';
import ThemeGallery from './ThemeGallery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════
// THEME COLORS (30+)
// ═══════════════════════════════════════════════════
const themeColors = [
  { name: 'Violet', value: '#8B5CF6' }, { name: 'Fuchsia', value: '#D946EF' },
  { name: 'Blue', value: '#3B82F6' }, { name: 'Emerald', value: '#10B981' },
  { name: 'Orange', value: '#F97316' }, { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' }, { name: 'Cyan', value: '#06B6D4' },
  { name: 'Amber', value: '#F59E0B' }, { name: 'Lime', value: '#84CC16' },
  { name: 'Teal', value: '#14B8A6' }, { name: 'Indigo', value: '#6366F1' },
  { name: 'Rose', value: '#F43F5E' }, { name: 'Gold', value: '#D4AF37' },
  { name: 'Neon Green', value: '#00FF88' }, { name: 'White', value: '#FFFFFF' },
  { name: 'Electric Blue', value: '#0066FF' }, { name: 'Hot Magenta', value: '#FF0090' },
  { name: 'Turquoise', value: '#40E0D0' }, { name: 'Coral', value: '#FF6F61' },
  { name: 'Peach', value: '#FFAB76' }, { name: 'Mint', value: '#3EB489' },
  { name: 'Lavender', value: '#B388FF' }, { name: 'Sky', value: '#38BDF8' },
  { name: 'Crimson', value: '#DC143C' }, { name: 'Tangerine', value: '#FF9F43' },
  { name: 'Chartreuse', value: '#7FFF00' }, { name: 'Aqua', value: '#00FFFF' },
  { name: 'Salmon', value: '#FA8072' }, { name: 'Plum', value: '#9B59B6' },
  { name: 'Marigold', value: '#FFC300' }, { name: 'Ice Blue', value: '#A5F3FC' },
];

const backgroundColors = [
  { name: 'Deep Black', value: '#050505' }, { name: 'Rich Black', value: '#0a0a0a' },
  { name: 'Dark Gray', value: '#121212' }, { name: 'Charcoal', value: '#1a1a2e' },
  { name: 'Navy', value: '#0f172a' }, { name: 'Dark Teal', value: '#042f2e' },
  { name: 'Dark Purple', value: '#1e1b4b' }, { name: 'Dark Wine', value: '#2d1b2e' },
  { name: 'Warm Beige', value: '#f3f3f1' }, { name: 'Pure White', value: '#FFFFFF' },
  { name: 'Cream', value: '#FFFBEB' }, { name: 'Snow', value: '#FFFEF5' },
  { name: 'Soft Pink', value: '#F5D5D5' }, { name: 'Sky Blue', value: '#D4E8F0' },
  { name: 'Lavender', value: '#E0D5F0' }, { name: 'Mint', value: '#D1FAE5' },
  { name: 'Peach', value: '#FFE4C9' }, { name: 'Sage', value: '#D4E2D4' },
  { name: 'Blush', value: '#FFE0E6' }, { name: 'Sand', value: '#E8DCC8' },
];

const gradientBackgrounds = [
  // Vibrant Dark
  { name: 'Neon Pulse', value: 'linear-gradient(135deg, #0D0D0D, #1a0030, #FF0090, #0D0D0D)', category: 'dark' },
  { name: 'Cyber Glow', value: 'linear-gradient(135deg, #000000, #0a0a2e, #00FFFF, #0a0a2e)', category: 'dark' },
  { name: 'Lava Flow', value: 'linear-gradient(135deg, #0f0000, #4a0000, #FF4500, #FF8C00)', category: 'dark' },
  { name: 'Deep Space', value: 'linear-gradient(180deg, #000000, #0f0f2e, #1e1b4b)', category: 'dark' },
  { name: 'Neon Night', value: 'linear-gradient(180deg, #0a0015, #1a0030, #4a0080)', category: 'dark' },
  { name: 'Midnight', value: 'linear-gradient(180deg, #020617, #1e1b4b, #312e81)', category: 'dark' },
  { name: 'Dark Ember', value: 'linear-gradient(135deg, #0D0000, #3d0c02, #b91c1c)', category: 'dark' },
  { name: 'Abyss', value: 'linear-gradient(180deg, #000000, #001a1a, #003333)', category: 'dark' },
  // Vibrant Multi-color
  { name: 'Sunset Fire', value: 'linear-gradient(135deg, #FF512F, #F09819, #FF0090)', category: 'vibrant' },
  { name: 'Electric Violet', value: 'linear-gradient(135deg, #4776E6, #8E54E9, #FF0090)', category: 'vibrant' },
  { name: 'Tropical', value: 'linear-gradient(135deg, #00B4DB, #0083B0, #00FF88)', category: 'vibrant' },
  { name: 'Mango Tango', value: 'linear-gradient(135deg, #FFE259, #FFA751, #FF6B6B)', category: 'vibrant' },
  { name: 'Berry Blast', value: 'linear-gradient(135deg, #8E2DE2, #FF0090, #FF6A00)', category: 'vibrant' },
  { name: 'Ocean Breeze', value: 'linear-gradient(135deg, #2E3192, #1BFFFF)', category: 'vibrant' },
  { name: 'Flamingo', value: 'linear-gradient(135deg, #FC5C7D, #6A82FB)', category: 'vibrant' },
  { name: 'Citrus Pop', value: 'linear-gradient(135deg, #f5af19, #f12711)', category: 'vibrant' },
  { name: 'Paradise', value: 'linear-gradient(135deg, #1CB5E0, #000851)', category: 'vibrant' },
  { name: 'Candy Rush', value: 'linear-gradient(135deg, #F857A6, #FF5858, #FFAD5C)', category: 'vibrant' },
  { name: 'Aurora', value: 'linear-gradient(135deg, #1e1b4b, #5b21b6, #06b6d4)', category: 'vibrant' },
  { name: 'Neon Peach', value: 'linear-gradient(135deg, #FFB199, #FF0844)', category: 'vibrant' },
  // Soft / Light
  { name: 'Cotton Candy', value: 'linear-gradient(135deg, #fce4ec, #e0d5f0, #d4e8f0)', category: 'soft' },
  { name: 'Blush Sky', value: 'linear-gradient(180deg, #fce4ec, #f3e8ff, #dbeafe)', category: 'soft' },
  { name: 'Rose Gold', value: 'linear-gradient(135deg, #f5d5d5, #e8b4b8, #d4a373)', category: 'soft' },
  { name: 'Peach Dream', value: 'linear-gradient(135deg, #FFDEE9, #B5FFFC)', category: 'soft' },
  { name: 'Lavender Mist', value: 'linear-gradient(135deg, #E8DAEF, #D4E6F1, #FADBD8)', category: 'soft' },
  { name: 'Morning Sky', value: 'linear-gradient(135deg, #a8edea, #fed6e3)', category: 'soft' },
  { name: 'Cream Soda', value: 'linear-gradient(135deg, #FFF1EB, #ACE0F9)', category: 'soft' },
  { name: 'Soft Mint', value: 'linear-gradient(135deg, #D4FC79, #96E6A1)', category: 'soft' },
  { name: 'Cloud Nine', value: 'linear-gradient(135deg, #fbc2eb, #a6c1ee)', category: 'soft' },
  { name: 'Honey Dew', value: 'linear-gradient(135deg, #F3E7E9, #E3EEFF, #E8F5E9)', category: 'soft' },
  // Nature
  { name: 'Ocean', value: 'linear-gradient(135deg, #0f172a, #1e3a5f, #06b6d4)', category: 'nature' },
  { name: 'Forest', value: 'linear-gradient(135deg, #042f2e, #065f46, #84cc16)', category: 'nature' },
  { name: 'Dusk', value: 'linear-gradient(135deg, #1e1b4b, #831843, #f97316)', category: 'nature' },
  { name: 'Northern Lights', value: 'linear-gradient(135deg, #43cea2, #185a9d)', category: 'nature' },
  { name: 'Rainforest', value: 'linear-gradient(135deg, #0b8793, #360033)', category: 'nature' },
  { name: 'Sunset', value: 'linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)', category: 'nature' },
];

const gradientCategories = [
  { id: 'all', label: 'All' },
  { id: 'dark', label: 'Dark' },
  { id: 'vibrant', label: 'Vibrant' },
  { id: 'soft', label: 'Soft' },
  { id: 'nature', label: 'Nature' },
];

// ═══════════════════════════════════════════════════
// FONTS (Premium Collection)
// ═══════════════════════════════════════════════════
const fontOptions = [
  { name: 'Outfit', value: 'Outfit', category: 'modern' },
  { name: 'Space Grotesk', value: 'Space Grotesk', category: 'modern' },
  { name: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans', category: 'modern' },
  { name: 'Lexend', value: 'Lexend', category: 'modern' },
  { name: 'Syne', value: 'Syne', category: 'modern' },
  { name: 'Bricolage Grotesque', value: 'Bricolage Grotesque', category: 'modern' },
  { name: 'Poppins', value: 'Poppins', category: 'modern' },
  { name: 'Montserrat', value: 'Montserrat', category: 'modern' },
  { name: 'Playfair Display', value: 'Playfair Display', category: 'elegant' },
  { name: 'Cormorant', value: 'Cormorant Garamond', category: 'elegant' },
  { name: 'DM Serif Display', value: 'DM Serif Display', category: 'elegant' },
  { name: 'Instrument Serif', value: 'Instrument Serif', category: 'elegant' },
  { name: 'Libre Baskerville', value: 'Libre Baskerville', category: 'elegant' },
  { name: 'Lora', value: 'Lora', category: 'elegant' },
  { name: 'Righteous', value: 'Righteous', category: 'bold' },
  { name: 'Bebas Neue', value: 'Bebas Neue', category: 'bold' },
  { name: 'Fredoka One', value: 'Fredoka One', category: 'bold' },
  { name: 'Abril Fatface', value: 'Abril Fatface', category: 'bold' },
  { name: 'Archivo Black', value: 'Archivo Black', category: 'bold' },
  { name: 'Oswald', value: 'Oswald', category: 'bold' },
  { name: 'Raleway', value: 'Raleway', category: 'modern' },
  { name: 'Dancing Script', value: 'Dancing Script', category: 'elegant' },
  { name: 'Pacifico', value: 'Pacifico', category: 'bold' },
  { name: 'Satoshi', value: 'Satoshi', category: 'modern' },
];

const fontCategories = [
  { id: 'all', label: 'All' },
  { id: 'modern', label: 'Modern' },
  { id: 'elegant', label: 'Elegant' },
  { id: 'bold', label: 'Bold' },
];

// ═══════════════════════════════════════════════════
// CARD STYLES, BUTTONS, ANIMATIONS, PATTERNS
// ═══════════════════════════════════════════════════
const cardStyles = [
  { id: 'solid', label: 'Solid' },
  { id: 'glass', label: 'Glass' },
  { id: 'outlined', label: 'Outlined' },
  { id: 'shadow', label: 'Shadow' },
  { id: 'neon', label: 'Neon Glow' },
];

const buttonShapes = [
  { id: 'pill', label: 'Pill' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'sharp', label: 'Sharp' },
];

const animatedBgs = [
  { id: 'none', label: 'None', desc: '' },
  { id: 'particles', label: 'Particles', desc: 'Floating colored dots' },
  { id: 'gradient-shift', label: 'Color Shift', desc: 'Slow hue rotation' },
  { id: 'stars', label: 'Stars', desc: 'Twinkling starfield' },
  { id: 'aurora', label: 'Aurora', desc: 'Northern lights' },
  { id: 'matrix', label: 'Matrix', desc: 'Digital rain' },
  { id: 'fireflies', label: 'Fireflies', desc: 'Glowing orbs' },
  { id: 'waves', label: 'Waves', desc: 'Flowing waves' },
  { id: 'snow', label: 'Snow', desc: 'Gentle snowfall' },
];

const bgPatterns = [
  { id: 'none', label: 'None' },
  { id: 'dots', label: 'Dots' },
  { id: 'grid', label: 'Grid' },
  { id: 'diagonal', label: 'Diagonal' },
  { id: 'noise', label: 'Noise' },
  { id: 'hexagon', label: 'Hexagon' },
  { id: 'waves', label: 'Waves' },
];

// ═══════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════
const Appearance = ({ profile, setProfile, onPreviewTheme }) => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    bio: '',
    avatar_url: '',
    cover_image_url: '',
    theme_color: '#8B5CF6',
    background_color: '#050505',
    font_family: 'Fredoka One',
    card_style: 'solid',
    button_shape: 'pill',
    link_layout: 'list',
    show_badge: false,
    animated_bg: 'none',
    bg_pattern: 'none',
    video_header_url: '',
    countdown_date: '',
    countdown_label: '',
    visitor_count_visible: false,
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState(user?.username || '');
  const [savingUsername, setSavingUsername] = useState(false);
  const [fontFilter, setFontFilter] = useState('all');
  const [gradientFilter, setGradientFilter] = useState('all');
  const [previewingThemeId, setPreviewingThemeId] = useState(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        avatar_url: profile.avatar_url || '',
        cover_image_url: profile.cover_image_url || '',
        theme_color: profile.theme_color || '#8B5CF6',
        background_color: profile.background_color || '#050505',
        font_family: profile.font_family || 'Fredoka One',
        card_style: profile.card_style || 'solid',
        button_shape: profile.button_shape || 'pill',
        link_layout: profile.link_layout || 'list',
        show_badge: profile.show_badge || false,
        animated_bg: profile.animated_bg || 'none',
        bg_pattern: profile.bg_pattern || 'none',
        video_header_url: profile.video_header_url || '',
        countdown_date: profile.countdown_date || '',
        countdown_label: profile.countdown_label || '',
        visitor_count_visible: profile.visitor_count_visible || false,
      });
      setLoaded(true);
    }
  }, [profile]);

  const autoSave = useCallback(async (data) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile`, data);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [setProfile]);

  const handleChange = (field, value) => {
    if (!loaded) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSave({ [field]: value }), 800);
  };

  const handleImmediateChange = (field, value) => {
    if (!loaded) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    autoSave({ [field]: value });
  };

  const handleSaveUsername = async () => {
    const trimmed = newUsername.toLowerCase().trim();
    if (!trimmed || trimmed.length < 3) { toast.error('Username must be at least 3 characters'); return; }
    if (trimmed === user?.username) { setEditingUsername(false); return; }
    setSavingUsername(true);
    try {
      const res = await axios.put(`${API}/auth/username`, { username: trimmed });
      if (updateUser) updateUser(res.data);
      toast.success('Username updated!');
      setEditingUsername(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update username');
    } finally { setSavingUsername(false); }
  };

  const handlePreviewTheme = (preset) => {
    if (!preset) {
      setPreviewingThemeId(null);
      if (onPreviewTheme) onPreviewTheme(null);
      return;
    }
    setPreviewingThemeId(preset.id);
    if (onPreviewTheme) onPreviewTheme(preset.settings);
  };

  const handleApplyTheme = async (preset) => {
    setPreviewingThemeId(null);
    if (onPreviewTheme) onPreviewTheme(null);
    const fields = preset.settings;
    setFormData(prev => ({ ...prev, ...fields }));
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile`, fields);
      setProfile(response.data);
      toast.success(`"${preset.name}" theme applied!`);
    } catch {
      toast.error('Failed to apply theme');
    } finally {
      setSaving(false);
    }
  };

  const filteredFonts = fontFilter === 'all' ? fontOptions : fontOptions.filter(f => f.category === fontFilter);
  const filteredGradients = gradientFilter === 'all' ? gradientBackgrounds : gradientBackgrounds.filter(g => g.category === gradientFilter);

  const isLight = (c) => {
    if (!c || c.startsWith('linear')) return false;
    const hex = c.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Appearance</h1>
          <p className="text-[#A1A1AA] mt-1">Customize your profile page</p>
        </div>
        {saving && (
          <span className="text-sm text-violet-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" /> Saving...
          </span>
        )}
      </div>

      {/* ════════ PROFILE INFO ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Profile Info</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-[#EDEDED]">Profile Link</Label>
            {!editingUsername ? (
              <div data-testid="username-display" onClick={() => { setEditingUsername(true); setNewUsername(user?.username || ''); }}
                className="mt-2 flex items-center gap-2 px-4 py-2.5 bg-[#1E1E1E] border border-[#27272A] rounded-lg cursor-pointer hover:border-violet-500/50 transition-colors group">
                <span className="text-[#A1A1AA]">createonelink.com/</span>
                <span className="text-white font-medium">{user?.username}</span>
                <Pencil className="w-4 h-4 text-[#71717A] ml-auto group-hover:text-violet-400 transition-colors" />
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex items-center flex-1 bg-[#1E1E1E] border border-violet-500 rounded-lg overflow-hidden">
                  <span className="text-[#A1A1AA] pl-4 text-sm">createonelink.com/</span>
                  <Input data-testid="username-edit-input" value={newUsername} onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="border-0 bg-transparent text-white focus-visible:ring-0 px-1" placeholder="username" maxLength={30} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveUsername(); if (e.key === 'Escape') setEditingUsername(false); }} />
                </div>
                <Button data-testid="save-username-btn" onClick={handleSaveUsername} disabled={savingUsername} size="sm" className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg">{savingUsername ? '...' : 'Save'}</Button>
                <Button variant="outline" size="sm" onClick={() => setEditingUsername(false)} className="border-[#27272A] text-[#A1A1AA] rounded-lg">Cancel</Button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-[#EDEDED]">Bio</Label>
            <Textarea data-testid="bio-input" value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="Tell visitors about yourself" rows={3} maxLength={150} />
            <p className="text-xs text-[#71717A] mt-1">{formData.bio?.length || 0}/150</p>
          </div>
          <div>
            <Label className="text-[#EDEDED]">Hero Image</Label>
            <div className="mt-2">
              <ImageUpload value={formData.avatar_url} onChange={(url) => { handleImmediateChange('avatar_url', url); if (!formData.cover_image_url) handleImmediateChange('cover_image_url', url); }} label="Hero Image" shape="rect" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* ════════ THEME COLOR ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-5 h-5 text-violet-400" />
          <h2 className="font-outfit font-semibold text-lg text-white">Theme Color</h2>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {themeColors.map(color => (
            <button key={color.value} data-testid={`theme-color-${color.value}`} onClick={() => handleImmediateChange('theme_color', color.value)}
              className="relative w-10 h-10 rounded-full transition-transform hover:scale-110" style={{ backgroundColor: color.value }}>
              {formData.theme_color === color.value && <div className="absolute inset-0 flex items-center justify-center"><Check className="w-5 h-5 text-white drop-shadow-lg" /></div>}
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="custom-theme-color" type="color" value={formData.theme_color} onChange={(e) => handleChange('theme_color', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent" />
          <Input value={formData.theme_color} onChange={(e) => handleChange('theme_color', e.target.value)} className="w-28 bg-[#1E1E1E] border-[#27272A] text-white uppercase" maxLength={7} />
        </div>
      </motion.div>

      {/* ════════ BACKGROUND ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Background</h2>
        <p className="text-sm text-[#A1A1AA] mb-3">Solid Colors</p>
        <div className="grid grid-cols-5 gap-3">
          {backgroundColors.map(color => (
            <button key={color.value} data-testid={`bg-color-${color.value}`} onClick={() => handleImmediateChange('background_color', color.value)}
              className="relative h-14 rounded-xl transition-transform hover:scale-105 border-2"
              style={{ backgroundColor: color.value, borderColor: formData.background_color === color.value ? '#8B5CF6' : '#27272A' }}>
              {formData.background_color === color.value && <div className="absolute inset-0 flex items-center justify-center"><Check className="w-5 h-5 drop-shadow-lg" style={{ color: isLight(color.value) ? '#000' : '#fff' }} /></div>}
              <span className="absolute bottom-0.5 left-0 right-0 text-center" style={{ fontSize: '0.5rem', color: isLight(color.value) ? '#555' : '#71717A' }}>{color.name}</span>
            </button>
          ))}
        </div>

        <p className="text-sm text-[#A1A1AA] mb-3 mt-6">Gradients</p>
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {gradientCategories.map(cat => (
            <button key={cat.id} data-testid={`grad-cat-${cat.id}`} onClick={() => setGradientFilter(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${gradientFilter === cat.id ? 'bg-violet-500 text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}>
              {cat.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
          {filteredGradients.map(grad => (
            <button key={grad.name} data-testid={`bg-gradient-${grad.name.toLowerCase().replace(/\s/g,'-')}`} onClick={() => handleImmediateChange('background_color', grad.value)}
              className="relative h-16 rounded-xl transition-transform hover:scale-105 border-2"
              style={{ background: grad.value, borderColor: formData.background_color === grad.value ? '#8B5CF6' : '#27272A' }}>
              {formData.background_color === grad.value && <div className="absolute inset-0 flex items-center justify-center"><Check className="w-5 h-5 text-white drop-shadow-lg" /></div>}
              <span className="absolute bottom-0.5 left-0 right-0 text-center" style={{ fontSize: '0.5rem', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{grad.name}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input data-testid="custom-bg-color" type="color" value={formData.background_color.startsWith('#') ? formData.background_color : '#050505'} onChange={(e) => handleChange('background_color', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent" />
          <Input value={formData.background_color} onChange={(e) => handleChange('background_color', e.target.value)} className="w-28 bg-[#1E1E1E] border-[#27272A] text-white" maxLength={100} />
        </div>
      </motion.div>

      {/* ════════ FONT ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Type className="w-5 h-5 text-violet-400" />
          <h2 className="font-outfit font-semibold text-lg text-white">Font</h2>
        </div>
        <div className="flex gap-2 mb-4 mt-3">
          {fontCategories.map(cat => (
            <button key={cat.id} data-testid={`font-cat-${cat.id}`} onClick={() => setFontFilter(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${fontFilter === cat.id ? 'bg-violet-500 text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}>
              {cat.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {filteredFonts.map(font => (
            <button key={font.value} data-testid={`font-${font.value.replace(/\s/g,'-')}`} onClick={() => handleImmediateChange('font_family', font.value)}
              className="p-3 rounded-xl border-2 transition-all text-center hover:scale-105"
              style={{ fontFamily: font.value, borderColor: formData.font_family === font.value ? '#8B5CF6' : '#27272A', backgroundColor: formData.font_family === font.value ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
              <span className="text-white text-sm">{font.name}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ════════ CARD STYLE ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Card Style</h2>
        <div className="grid grid-cols-5 gap-3">
          {cardStyles.map(style => (
            <button key={style.id} data-testid={`card-style-${style.id}`} onClick={() => handleImmediateChange('card_style', style.id)}
              className={`p-4 rounded-xl border-2 transition-all text-center ${
                style.id === 'glass' ? 'backdrop-blur-sm bg-white/10' :
                style.id === 'outlined' ? 'bg-transparent border-dashed' :
                style.id === 'shadow' ? 'shadow-lg shadow-black/20 bg-[#1E1E1E]' :
                style.id === 'neon' ? 'bg-[#1E1E1E] shadow-[0_0_15px_rgba(139,92,246,0.3)]' :
                'bg-[#1E1E1E]'
              }`}
              style={{ borderColor: formData.card_style === style.id ? '#8B5CF6' : '#27272A' }}>
              <span className="text-white text-xs font-medium">{style.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ════════ BUTTON SHAPE ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Button Shape</h2>
        <div className="grid grid-cols-3 gap-3">
          {buttonShapes.map(shape => (
            <button key={shape.id} data-testid={`btn-shape-${shape.id}`} onClick={() => handleImmediateChange('button_shape', shape.id)}
              className="p-3 border-2 transition-all text-center"
              style={{ borderRadius: shape.id === 'pill' ? '9999px' : shape.id === 'rounded' ? '12px' : '4px', borderColor: formData.button_shape === shape.id ? '#8B5CF6' : '#27272A', backgroundColor: formData.button_shape === shape.id ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
              <span className="text-white text-sm">{shape.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ════════ LINK LAYOUT ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Link Layout</h2>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="link-layout-list" onClick={() => handleImmediateChange('link_layout', 'list')}
            className="p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2"
            style={{ borderColor: formData.link_layout === 'list' ? '#8B5CF6' : '#27272A', backgroundColor: formData.link_layout === 'list' ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
            <List className="w-5 h-5 text-white" />
            <span className="text-white text-sm font-medium">List</span>
            <div className="w-full space-y-1.5 mt-1">
              <div className="w-full h-3 rounded-full bg-white/10" />
              <div className="w-full h-3 rounded-full bg-white/10" />
              <div className="w-full h-3 rounded-full bg-white/10" />
            </div>
          </button>
          <button data-testid="link-layout-grid" onClick={() => handleImmediateChange('link_layout', 'grid')}
            className="p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2"
            style={{ borderColor: formData.link_layout === 'grid' ? '#8B5CF6' : '#27272A', backgroundColor: formData.link_layout === 'grid' ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
            <LayoutGrid className="w-5 h-5 text-white" />
            <span className="text-white text-sm font-medium">Grid</span>
            <div className="grid grid-cols-2 gap-1 mt-1 w-full">
              <div className="aspect-square rounded-lg bg-white/10" />
              <div className="aspect-square rounded-lg bg-white/10" />
              <div className="aspect-square rounded-lg bg-white/10" />
              <div className="aspect-square rounded-lg bg-white/10" />
            </div>
          </button>
        </div>
      </motion.div>

      {/* ════════ ANIMATED BACKGROUND ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-violet-400" />
          <h2 className="font-outfit font-semibold text-lg text-white">Animated Background</h2>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {animatedBgs.map(bg => (
            <button key={bg.id} data-testid={`animated-bg-${bg.id}`} onClick={() => handleImmediateChange('animated_bg', bg.id)}
              className="p-3 rounded-xl border-2 transition-all text-left"
              style={{ borderColor: formData.animated_bg === bg.id ? '#8B5CF6' : '#27272A', backgroundColor: formData.animated_bg === bg.id ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
              <span className="text-white text-xs font-medium block">{bg.label}</span>
              {bg.desc && <span className="text-[#71717A] text-[10px]">{bg.desc}</span>}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ════════ BACKGROUND PATTERN ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-5 h-5 text-violet-400" />
          <h2 className="font-outfit font-semibold text-lg text-white">Background Pattern</h2>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {bgPatterns.map(p => (
            <button key={p.id} data-testid={`bg-pattern-${p.id}`} onClick={() => handleImmediateChange('bg_pattern', p.id)}
              className="p-3 rounded-xl border-2 transition-all text-center"
              style={{ borderColor: formData.bg_pattern === p.id ? '#8B5CF6' : '#27272A', backgroundColor: formData.bg_pattern === p.id ? 'rgba(139,92,246,0.1)' : '#1E1E1E' }}>
              <span className="text-white text-xs">{p.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ════════ EXTRAS ════════ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 space-y-5">
        <h2 className="font-outfit font-semibold text-lg text-white">Extras</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">Verified Badge</p>
            <p className="text-[#71717A] text-xs">Show a checkmark next to your name</p>
          </div>
          <button data-testid="toggle-badge" onClick={() => handleImmediateChange('show_badge', !formData.show_badge)}
            className={`w-11 h-6 rounded-full transition-all ${formData.show_badge ? 'bg-violet-500' : 'bg-[#27272A]'}`}>
            <div className="w-5 h-5 rounded-full bg-white transition-all" style={{ transform: `translateX(${formData.show_badge ? '22px' : '2px'})` }} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">Visitor Counter</p>
            <p className="text-[#71717A] text-xs">Show profile view count</p>
          </div>
          <button data-testid="toggle-visitor-count" onClick={() => handleImmediateChange('visitor_count_visible', !formData.visitor_count_visible)}
            className={`w-11 h-6 rounded-full transition-all ${formData.visitor_count_visible ? 'bg-violet-500' : 'bg-[#27272A]'}`}>
            <div className="w-5 h-5 rounded-full bg-white transition-all" style={{ transform: `translateX(${formData.visitor_count_visible ? '22px' : '2px'})` }} />
          </button>
        </div>
        <div>
          <p className="text-white font-medium text-sm mb-2">Countdown Timer</p>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="countdown-label-input" value={formData.countdown_label} onChange={(e) => handleChange('countdown_label', e.target.value)} className="bg-[#1E1E1E] border-[#27272A] text-white" placeholder="e.g. Launch Day!" />
            <Input data-testid="countdown-date-input" type="datetime-local" value={formData.countdown_date} onChange={(e) => handleChange('countdown_date', e.target.value)} className="bg-[#1E1E1E] border-[#27272A] text-white" />
          </div>
        </div>
        <div>
          <p className="text-white font-medium text-sm mb-2">Video Header URL</p>
          <Input data-testid="video-header-input" value={formData.video_header_url} onChange={(e) => handleChange('video_header_url', e.target.value)} className="bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://youtube.com/watch?v=..." />
        </div>
      </motion.div>
    </div>
  );
};

export default Appearance;
